import { useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../lib/api.js'
import { dateTime } from '../../../lib/format.js'
import { Badge, Card, Drawer, DrawerSection, Empty, Field, Menu, MenuItem, Notice, PageHead, RowsSkeleton, Segmented, Tabs } from '../../../components/ui.js'
import { ConfirmDialog, toast } from '../../../components/feedback.js'
import { MailIcon, MoreIcon, PlusIcon, RefreshIcon, WhatsAppIcon } from '../../../components/icons.js'
import { TemplatePreviewPane, useTemplates } from './Wizard.js'
import { StatusBadge, errorMessage, type Template } from './shared.js'

interface WakitTemplates {
  configured: boolean
  error: string | null
  templates: Array<{ name: string; language: string; status: string; sendable: boolean; body: string | null; variables: number | null }>
}

const APPROVAL_TONE: Record<string, string | undefined> = { approved: 'ok', not_applicable: undefined, unlinked: 'warn', pending: 'warn', rejected: 'failed', not_found: 'failed' }

function blank(channel: 'email' | 'whatsapp'): Template {
  return {
    id: '',
    name: '',
    channel,
    language: 'en',
    subject: channel === 'email' ? 'Your wallet balance is low' : null,
    body: channel === 'email' ? '<p>Hello {{customer_name}},</p>\n<p>Your wallet balance is {{wallet_balance}}.</p>' : 'Hi {{customer_name}}, your wallet balance is {{wallet_balance}}.',
    textBody: null,
    variables: channel === 'whatsapp' ? ['customer_name', 'wallet_balance'] : [],
    providerTemplateId: null,
    approvalStatus: channel === 'email' ? 'not_applicable' : 'unlinked',
    version: 1,
    status: 'draft',
    updatedAt: new Date().toISOString(),
  }
}

export function AdminWalletAlertTemplatesPage(): React.ReactNode {
  const [params, setParams] = useSearchParams()
  const queryClient = useQueryClient()
  const templates = useTemplates()
  const [channel, setChannel] = useState<'all' | 'email' | 'whatsapp'>('all')
  const [editing, setEditing] = useState<Template | null>(null)
  const [deleting, setDeleting] = useState<Template | null>(null)

  const wakit = useQuery({
    queryKey: ['admin', 'wallet-alerts', 'wakit-templates'],
    queryFn: () => api<WakitTemplates>('/v1/admin/wallet-alerts/wakit-templates'),
    staleTime: 60_000,
  })

  const editId = params.get('edit')
  if (editId && !editing && templates.data) {
    const found = templates.data.items.find((t) => t.id === editId)
    if (found) setEditing(found)
  }

  const refresh = (): void => void queryClient.invalidateQueries({ queryKey: ['admin', 'wallet-alerts'] })

  const duplicate = useMutation({
    mutationFn: (id: string) => api<Template>(`/v1/admin/wallet-alerts/templates/${id}/duplicate`, { method: 'POST' }),
    onSuccess: (t) => {
      toast('ok', `Duplicated as “${t.name}”`)
      refresh()
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => api(`/v1/admin/wallet-alerts/templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('ok', 'Template deleted')
      setDeleting(null)
      refresh()
    },
    onError: (error) => {
      setDeleting(null)
      toast('error', 'Could not delete', errorMessage(error))
    },
  })

  const items = (templates.data?.items ?? []).filter((t) => channel === 'all' || t.channel === channel)

  return (
    <>
      <PageHead
        title="Alert Templates"
        subtitle="Reusable WhatsApp and email messages for wallet alerts. Every save keeps a version."
        crumbs={[{ label: 'Wallet Alerts', to: '/admin/wallet-alerts' }, { label: 'Templates' }]}
        actions={
          <div className="wa-head-actions">
            <button type="button" className="btn" disabled={wakit.isFetching} onClick={() => void wakit.refetch().then(refresh)}>
              <RefreshIcon size={16} /> Sync WhatsApp approvals
            </button>
            <button type="button" className="btn" onClick={() => setEditing(blank('whatsapp'))}>
              <PlusIcon size={16} /> WhatsApp template
            </button>
            <button type="button" className="btn btn--primary" onClick={() => setEditing(blank('email'))}>
              <PlusIcon size={16} /> Email template
            </button>
          </div>
        }
      />

      {wakit.data && !wakit.data.configured && (
        <Notice kind="warn" title="WhatsApp (Wakit) is not configured">
          WhatsApp templates can be drafted, but approval cannot be checked and nothing can be sent until Wakit is set up in Settings → OTP.
        </Notice>
      )}
      {wakit.data?.error && wakit.data.configured && <Notice kind="error" title="Could not read Wakit templates">{wakit.data.error}</Notice>}

      <Card flush>
        <Tabs
          value={channel}
          onChange={setChannel}
          tabs={[
            { key: 'all', label: 'All' },
            { key: 'whatsapp', label: 'WhatsApp' },
            { key: 'email', label: 'Email' },
          ]}
        />
        {templates.isPending && <RowsSkeleton rows={5} />}
        {templates.data && items.length === 0 && <Empty title="No templates" />}
        {items.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Template</th>
                  <th>Channel</th>
                  <th>Language</th>
                  <th>Approval</th>
                  <th>Status</th>
                  <th className="right">Used by</th>
                  <th>Updated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((t) => (
                  <tr key={t.id} className="is-clickable" onClick={() => setEditing(t)}>
                    <td>
                      <strong>{t.name}</strong>
                      <span className="table__sub">
                        v{t.version}
                        {t.providerTemplateId ? ` · Wakit: ${t.providerTemplateId}` : ''}
                      </span>
                    </td>
                    <td>
                      <span className="row" style={{ gap: 6 }}>
                        {t.channel === 'email' ? <MailIcon size={15} /> : <WhatsAppIcon size={15} />}
                        {t.channel === 'email' ? 'Email' : 'WhatsApp'}
                      </span>
                    </td>
                    <td>{t.language}</td>
                    <td>
                      <Badge tone={APPROVAL_TONE[t.approvalStatus]}>{t.approvalStatus.replace(/_/g, ' ')}</Badge>
                    </td>
                    <td>
                      <StatusBadge status={t.status} />
                    </td>
                    <td className="right num">{t.usedBy ?? 0} rule{t.usedBy === 1 ? '' : 's'}</td>
                    <td>{dateTime(t.updatedAt)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <Menu trigger={<MoreIcon size={18} />} label={`Actions for ${t.name}`}>
                        <MenuItem onClick={() => setEditing(t)}>Edit</MenuItem>
                        <MenuItem onClick={() => duplicate.mutate(t.id)}>Duplicate</MenuItem>
                        <MenuItem danger onClick={() => setDeleting(t)}>
                          Delete
                        </MenuItem>
                      </Menu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <TemplateEditor
          template={editing}
          wakit={wakit.data}
          variables={templates.data?.variables ?? []}
          onClose={() => {
            setEditing(null)
            if (params.get('edit')) setParams({})
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Delete this template?"
        message={`“${deleting?.name}” and its version history will be removed. Templates used by a rule cannot be deleted.`}
        confirmLabel="Delete template"
        danger
        pending={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        onClose={() => setDeleting(null)}
      />
    </>
  )
}

interface TemplateVersion {
  version: number
  subject: string | null
  body: string
  textBody: string | null
  providerTemplateId: string | null
  createdAt: string
}

function TemplateEditor({
  template,
  wakit,
  variables,
  onClose,
}: {
  template: Template
  wakit: WakitTemplates | undefined
  variables: Array<{ key: string; label: string }>
  onClose: () => void
}): React.ReactNode {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<Template>(template)
  const [view, setView] = useState<'edit' | 'preview' | 'versions' | 'test'>('edit')
  const [testTo, setTestTo] = useState('')
  const bodyRef = useRef<HTMLTextAreaElement>(null)
  const isNew = template.id === ''

  const versions = useQuery({
    queryKey: ['admin', 'wallet-alerts', 'template', template.id],
    queryFn: () => api<{ versions: TemplateVersion[] }>(`/v1/admin/wallet-alerts/templates/${template.id}`),
    enabled: !isNew && view === 'versions',
  })

  const save = useMutation({
    mutationFn: (status: 'draft' | 'active') => {
      const body = {
        name: draft.name,
        channel: draft.channel,
        language: draft.language,
        subject: draft.channel === 'email' ? draft.subject : null,
        body: draft.body,
        textBody: draft.textBody,
        variables: draft.variables,
        providerTemplateId: draft.channel === 'whatsapp' ? draft.providerTemplateId : null,
        status,
      }
      return isNew
        ? api<Template>('/v1/admin/wallet-alerts/templates', { method: 'POST', body })
        : api<Template>(`/v1/admin/wallet-alerts/templates/${template.id}`, { method: 'PUT', body })
    },
    onSuccess: (saved, status) => {
      toast('ok', status === 'active' ? 'Template saved and active' : 'Saved as draft', `Version ${saved.version}`)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'wallet-alerts'] })
      onClose()
    },
    onError: (error) => toast('error', 'Could not save the template', errorMessage(error)),
  })

  const test = useMutation({
    mutationFn: () => api('/v1/admin/wallet-alerts/test-send', { method: 'POST', body: { channel: draft.channel, templateId: template.id, recipient: testTo } }),
    onSuccess: () => toast('ok', 'Test sent', `To ${testTo} only, with sample merchant data.`),
    onError: (error) => toast('error', 'Test failed', errorMessage(error)),
  })

  function insert(key: string): void {
    const token = `{{${key}}}`
    const el = bodyRef.current
    if (!el) {
      setDraft({ ...draft, body: draft.body + token })
      return
    }
    const start = el.selectionStart ?? draft.body.length
    const end = el.selectionEnd ?? start
    const next = draft.body.slice(0, start) + token + draft.body.slice(end)
    setDraft({ ...draft, body: next })
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(start + token.length, start + token.length)
    })
  }

  const linked = wakit?.templates.find((t) => t.name === draft.providerTemplateId)
  const approved = draft.channel === 'email' || Boolean(linked && (linked.sendable || linked.status === 'approved'))
  const used = new Set([...draft.body.matchAll(/\{\{\s*([a-z_]+)\s*\}\}/g)].map((m) => m[1]))
  const missingRequired = draft.channel === 'email' && !used.has('wallet_balance')

  return (
    <Drawer
      open
      onClose={onClose}
      title={isNew ? `New ${draft.channel === 'email' ? 'email' : 'WhatsApp'} template` : draft.name}
      subtitle={isNew ? undefined : `Version ${template.version} · ${template.status}`}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn" disabled={save.isPending || draft.name.trim().length < 2} onClick={() => save.mutate('draft')}>
            Save as draft
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={save.isPending || draft.name.trim().length < 2 || !approved}
            title={approved ? undefined : 'Link an approved Wakit template first'}
            onClick={() => save.mutate('active')}
          >
            Save & activate
          </button>
        </>
      }
    >
      <Segmented
        label="Editor view"
        value={view}
        onChange={setView}
        options={[
          { key: 'edit', label: 'Edit' },
          { key: 'preview', label: 'Preview' },
          ...(isNew ? [] : [{ key: 'test' as const, label: 'Test' }, { key: 'versions' as const, label: 'Versions' }]),
        ]}
      />

      {view === 'edit' && (
        <>
          <DrawerSection title="Template">
            <div className="wa-form">
              <Field label="Name">
                <input className="input" value={draft.name} maxLength={120} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </Field>
              <Field label="Language">
                <select className="select" value={draft.language} onChange={(e) => setDraft({ ...draft, language: e.target.value })}>
                  {['en', 'en_US', 'hi', 'mr', 'ta', 'te', 'bn', 'gu'].map((lang) => (
                    <option key={lang} value={lang}>
                      {lang}
                    </option>
                  ))}
                </select>
              </Field>
              {draft.channel === 'email' && (
                <Field label="Subject">
                  <input className="input" value={draft.subject ?? ''} maxLength={200} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
                </Field>
              )}
            </div>
          </DrawerSection>

          {draft.channel === 'whatsapp' && (
            <DrawerSection title="Approved WhatsApp template">
              <div className="wa-form">
                <Notice kind={approved ? 'ok' : 'warn'} title={approved ? 'Linked to an approved template' : 'Not usable in production yet'}>
                  WhatsApp only allows business-initiated messages through templates Meta has approved. Pick the approved Wakit template this maps to;
                  the body below is your preview copy of it.
                </Notice>
                <Field label="Wakit template">
                  <select className="select" value={draft.providerTemplateId ?? ''} onChange={(e) => setDraft({ ...draft, providerTemplateId: e.target.value || null })}>
                    <option value="">Not linked</option>
                    {(wakit?.templates ?? []).map((t) => (
                      <option key={t.name} value={t.name}>
                        {t.name} — {t.sendable ? 'approved' : t.status || 'unknown'} ({t.language})
                        {t.variables !== null ? ` · ${t.variables} variables` : ''}
                      </option>
                    ))}
                    {draft.providerTemplateId && !linked && <option value={draft.providerTemplateId}>{draft.providerTemplateId} (not found in Wakit)</option>}
                  </select>
                </Field>
                {linked?.body && (
                  <Field label="Approved wording (from Wakit)">
                    <pre className="wa-code">{linked.body}</pre>
                  </Field>
                )}
                <Field label="Parameter mapping" hint="Which variable fills {{1}}, {{2}} … of the approved template, in order.">
                  <div className="wa-form">
                    {draft.variables.map((key, index) => (
                      <div key={index} className="row" style={{ gap: 6 }}>
                        <code style={{ width: 44 }}>{`{{${index + 1}}}`}</code>
                        <select
                          className="select"
                          value={key}
                          onChange={(e) => setDraft({ ...draft, variables: draft.variables.map((v, i) => (i === index ? e.target.value : v)) })}
                        >
                          {variables.map((v) => (
                            <option key={v.key} value={v.key}>
                              {v.key} — {v.label}
                            </option>
                          ))}
                        </select>
                        <button type="button" className="btn btn--sm" onClick={() => setDraft({ ...draft, variables: draft.variables.filter((_, i) => i !== index) })}>
                          Remove
                        </button>
                      </div>
                    ))}
                    <div>
                      <button type="button" className="btn btn--sm" disabled={draft.variables.length >= 20} onClick={() => setDraft({ ...draft, variables: [...draft.variables, 'customer_name'] })}>
                        <PlusIcon size={14} /> Add parameter
                      </button>
                    </div>
                    {linked?.variables !== null && linked?.variables !== undefined && linked.variables !== draft.variables.length && (
                      <Notice kind="warn" title={`The approved template expects ${linked.variables} parameter(s); ${draft.variables.length} mapped.`} />
                    )}
                  </div>
                </Field>
              </div>
            </DrawerSection>
          )}

          <DrawerSection title={draft.channel === 'email' ? 'HTML body' : 'Preview body'}>
            <div className="wa-form">
              <div className="wa-vars">
                {variables.map((v) => (
                  <button key={v.key} type="button" title={v.label} onClick={() => insert(v.key)}>
                    {`{{${v.key}}}`}
                  </button>
                ))}
              </div>
              <textarea ref={bodyRef} className="textarea wa-textarea" value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
              {missingRequired && <Notice kind="warn" title="This email never states the balance">Add {'{{wallet_balance}}'} so the merchant knows what triggered it.</Notice>}
              {draft.channel === 'email' && (
                <Field label="Plain-text fallback" hint="For mail clients that do not render HTML. Blank strips the HTML tags.">
                  <textarea className="textarea wa-textarea" style={{ minHeight: 120 }} value={draft.textBody ?? ''} onChange={(e) => setDraft({ ...draft, textBody: e.target.value || null })} />
                </Field>
              )}
            </div>
          </DrawerSection>
        </>
      )}

      {view === 'preview' && (
        <DrawerSection title="Live preview">
          <TemplatePreviewPane template={draft} />
        </DrawerSection>
      )}

      {view === 'test' && (
        <DrawerSection title="Send a test">
          <div className="wa-form">
            <Notice kind="warn" title="Real message, to you only">
              Sends the saved version of this template with sample merchant data to the address you type. Save changes first. 10 tests per hour.
            </Notice>
            <Field label={draft.channel === 'email' ? 'Test email' : 'Test WhatsApp number'}>
              <input className="input" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder={draft.channel === 'email' ? 'you@company.com' : '98765 43210'} />
            </Field>
            <button type="button" className="btn btn--primary" disabled={!testTo || test.isPending} onClick={() => test.mutate()}>
              {test.isPending ? 'Sending…' : 'Send test'}
            </button>
          </div>
        </DrawerSection>
      )}

      {view === 'versions' && (
        <DrawerSection title="Version history">
          {!versions.data ? (
            <RowsSkeleton rows={4} />
          ) : (
            <div className="wa-form">
              {versions.data.versions.map((version) => (
                <div key={version.version} className="wa-group">
                  <div className="wa-group__head">
                    <span>
                      Version {version.version}
                      {version.version === template.version ? ' (current)' : ''}
                    </span>
                    <span className="row" style={{ gap: 8 }}>
                      {dateTime(version.createdAt)}
                      {version.version !== template.version && (
                        <button
                          type="button"
                          className="btn btn--sm"
                          onClick={() => {
                            setDraft({ ...draft, subject: version.subject, body: version.body, textBody: version.textBody, providerTemplateId: version.providerTemplateId ?? draft.providerTemplateId })
                            setView('edit')
                            toast('info', `Loaded version ${version.version}`, 'Save to make it the current version.')
                          }}
                        >
                          Restore
                        </button>
                      )}
                    </span>
                  </div>
                  {version.subject && <strong style={{ fontSize: '.82rem' }}>{version.subject}</strong>}
                  <pre className="wa-code" style={{ maxHeight: 140 }}>{version.body}</pre>
                </div>
              ))}
            </div>
          )}
        </DrawerSection>
      )}
    </Drawer>
  )
}
